import { roundToFirstDecimalPlace } from '../utils/roundToFirstDecimalPlace'
import type { Mutable, NonEmptyArray } from '../utils/types'

/**
 * Represents an anchor to an entity at a position and spanning a vertical range.
 * When moved, the anchor's height will change and collapse when untethered from
 * its original position.
 */
export interface Anchor {
  readonly top: number
  readonly height: number
}

/**
 * A VBar item that needs layout.
 * Items have content and a vertical anchor.
 * Only the minimum content height and the vertical anchor are used to calculate layout.
 */
export interface VBarItem {
  /** Unique identifier for the item */
  readonly id: string
  /**
   * The VBar anchor.
   * This should be used to detect overlap, but not for render.
   */
  readonly anchor: Anchor
  /**
   * Minimum height necessary to render the item content.
   * This is used during layout to ensure that items have enough space to render their content.
   */
  readonly minContentHeight: number
}

/**
 * A VBar item after layout.
 * It is part of a cluster of 1 or more items.
 * It should only be rendered if the cluster is a singleton or if the cluster is expanded.
 */
export interface VBarItemLayout extends Omit<VBarItem, 'minContentHeight'> {
  /** Position calculated after layout */
  readonly top: number
  /**
   * Available height to render the item.
   * This will always be greater than or equal to `minHeight`.
   * However, it can be less than the original height of the item when the item is part of a cluster.
   */
  readonly maxContentHeight: number
}

/**
 * A cluster of VBar items after layout.
 * Singleton clusters are expanded by default.
 */
export interface VBarClusterLayout<Item> {
  readonly id: string
  /** Position calculated after layout */
  readonly top: number
  /** The maximum height available to render the cluster in its current `expanded` state */
  readonly maxContentHeight: number
  /** Items in the cluster */
  readonly items: NonEmptyArray<Item>
  /** True when this cluster is expanded and the children should be rendered */
  readonly expanded: boolean
}

interface VBarItemLayoutInternal extends VBarItemLayout {
  /**
   * Minimum height necessary to render the item content.
   * This is used during layout to ensure that items have enough space to render their content.
   */
  readonly minContentHeight: number
}

interface VBarClusterLayoutInternal<Item> extends VBarClusterLayout<Item> {
  /** The minimum height needed to render the cluster in its current `expanded` state */
  readonly minContentHeight: number
}

export interface LayoutVBarItemsParams {
  /** Ordered list of VBar items to layout */
  readonly orderedItems: readonly VBarItem[]
  /**
   * Total height of the container to layout items in.
   * This is used to calculate the maximum height available for the last item.
   */
  readonly scrollHeight?: number
  /**
   * Set of cluster IDs that should be expanded.
   * If a cluster is in this set, it will be expanded.
   * This can be used to expand clusters that don't yet have any items in `expandedVBars`.
   */
  readonly expandedClusters?: ReadonlySet<string>
  /**
   * Set of VBar item IDs that should be expanded.
   * If an item is in this set, its cluster will be expanded.
   */
  readonly expandedVBars?: ReadonlySet<string>
  /**
   * A map of VBar IDs to their stable top positions.
   * This is used to ensure that active items maintain their position when a cluster is collapsed before them.
   * NOTE: A cluster that is expanded before the item can still push the item down, otherwise this would cause overlaps.
   */
  readonly stableVBarsTops?: ReadonlyMap<string, number>
  /**
   * Threshold for clustering items.
   * If the vertical distance between two item's anchors is less than this threshold,
   * the items will be clustered together.
   * Defaults to 0.
   */
  readonly clusterThreshold?: number
  /**
   * Gap between expanded clusters. This can be used to add spacing between clusters (individual VBars are considered singleton clusters).
   * Defaults to 0.
   */
  readonly interClusterGap?: number
  /**
   * Margin at the bottom of an expanded cluster. This can be used to add spacing after the items of an expanded cluster.
   * If `interClusterGap` is provided, the maximum value of either will be used as the bottom margin below an expanded cluster.
   * Defaults to 0.
   */
  readonly expandedClusterBottomMargin?: number
  /**
   * Minimum content height of a collapsed cluster.
   * If not provided, the minimum content height will be calculated based on the items in the cluster.
   */
  readonly collapsedClusterMinContentHeight?: number
}

/**
 * Layout VBar items into clusters based on their vertical position and height.
 * Each item will be placed in a cluster of 1 or more items that overlap vertically.
 * Overlap is determined by the vertical track of each item, and the `clusterGap` parameter.
 * Items will always be provided with a max height that is at least as large as their minimum content height.
 * When clusters are expanded, the items below it will be pushed down to ensure that the expanded cluster
 * can render all of its items with at least their minimum content height.
 */
export function layoutVBarItems({
  orderedItems,
  scrollHeight,
  collapsedClusterMinContentHeight,
  expandedClusters = new Set<string>(),
  expandedVBars = new Set<string>(),
  stableVBarsTops = new Map<string, number>(),
  clusterThreshold = 0,
  interClusterGap = 0,
  expandedClusterBottomMargin = 0,
}: LayoutVBarItemsParams): VBarClusterLayout<VBarItemLayout>[] {
  const clusters: Mutable<VBarClusterLayoutInternal<Mutable<VBarItemLayoutInternal>>>[] = []

  for (const item of orderedItems) {
    const clusterItem: Mutable<VBarItemLayoutInternal> = {
      ...item,
      top: item.anchor.top,
      maxContentHeight: item.minContentHeight,
    }
    const lastCluster = clusters.at(-1)
    const lastClusterItem = lastCluster?.items.at(-1)

    // If the item overlaps with the last cluster, add it to that cluster
    // and increase minContentHeight and trackHeight of the cluster
    if (
      lastCluster &&
      lastClusterItem &&
      // Clustering is based on overlap on the original anchors
      item.anchor.top <
        lastClusterItem.anchor.top + lastClusterItem.anchor.height + clusterThreshold
    ) {
      // Compute item positions assuming the cluster is expanded (items will be hidden if the cluster is collapsed)
      clusterItem.top = Math.max(
        // Honor min-content-height of the previous item
        lastClusterItem.top + lastClusterItem.minContentHeight,
        // Position at or after the anchor of the current item
        item.anchor.top,
      )
      lastClusterItem.maxContentHeight = clusterItem.top - lastClusterItem.top
      lastCluster.items.push(clusterItem)
      lastCluster.id = createClusterId(lastCluster.items)
      lastCluster.expanded =
        lastCluster.expanded ||
        expandedVBars.has(clusterItem.id) ||
        expandedClusters.has(lastCluster.id)
    }
    // Else create a new cluster for the item
    else {
      let itemTop = Math.max(stableVBarsTops.get(item.id) ?? 0, item.anchor.top)
      if (lastCluster) {
        itemTop = Math.max(
          finalizeClusterLayout(
            lastCluster,
            collapsedClusterMinContentHeight,
            interClusterGap,
            expandedClusterBottomMargin,
            item,
          ) ?? 0,
          itemTop,
        )
      }

      clusterItem.top = itemTop
      const clusterId = createClusterId([clusterItem])

      clusters.push({
        id: clusterId,
        top: itemTop,
        items: [clusterItem],
        minContentHeight: item.minContentHeight,
        maxContentHeight: item.minContentHeight,
        expanded: expandedVBars.has(clusterItem.id) || expandedClusters.has(clusterId),
      })
    }
  }

  const lastCluster = clusters.at(-1)
  if (lastCluster) {
    finalizeClusterLayout(
      lastCluster,
      collapsedClusterMinContentHeight,
      interClusterGap,
      expandedClusterBottomMargin,
      undefined,
      scrollHeight,
    )
  }

  return clusters.map(cluster => {
    return {
      id: cluster.id,
      top: roundToFirstDecimalPlace(cluster.top),
      maxContentHeight: roundToFirstDecimalPlace(cluster.maxContentHeight),
      expanded: cluster.expanded,
      items: cluster.items.map(item => ({
        id: item.id,
        top: roundToFirstDecimalPlace(item.top),
        anchor: {
          top: roundToFirstDecimalPlace(item.anchor.top),
          height: roundToFirstDecimalPlace(item.anchor.height),
        },
        maxContentHeight: roundToFirstDecimalPlace(item.maxContentHeight),
      })) as NonEmptyArray<VBarItemLayout>,
    }
  })
}

function createClusterId(items: readonly VBarItem[]): string {
  // Generate a unique cluster ID based on the items in the cluster
  return `cluster-${items.map(item => item.id).join('-')}`
}

/**
 * Finalizes the layout of a cluster by calculating its min-content-height and max-content-height.
 * This will also update the last item in the cluster to have the correct max-content-height.
 * If `nextItem` is provided, it will be used to calculate the max-content-height of the cluster.
 * If `totalHeight` is provided, it will be used to calculate the max-content-height of the cluster.
 * If `nextItem` is provided, the top of this item will be returned.
 */
function finalizeClusterLayout(
  cluster: Mutable<VBarClusterLayoutInternal<Mutable<VBarItemLayoutInternal>>>,
  collapsedClusterMinContentHeight?: number,
  interClusterGap?: number,
  expandedClusterBottomMargin?: number,
  nextItem?: VBarItem,
  scrollHeight?: number,
): number | undefined {
  // Auto-expand all singleton clusters
  cluster.expanded = cluster.expanded || cluster.items.length === 1

  const lastClusterItem = cluster.items[cluster.items.length - 1]

  const collapsedClusterMinHeight =
    collapsedClusterMinContentHeight ??
    // we default to the merged track height of the items
    lastClusterItem.anchor.top + lastClusterItem.anchor.height - cluster.items[0].anchor.top

  // Calculate the last cluster min-content-height
  // If expanded, use the last item's min-content-height and measure distance from the top of the cluster to the last item's bottom
  // If collapsed, used the provided collapsedClusterMinContentHeight
  cluster.minContentHeight = cluster.expanded
    ? lastClusterItem.top + lastClusterItem.minContentHeight - cluster.top
    : collapsedClusterMinHeight

  // Update max-content-height of the cluster and its last item
  if (nextItem) {
    // If there is a bottom margin to be added after the previous expanded cluster,
    // - remove the margin from the cluster's max-height if there is space for it (max-height has to stay > than min-content-height)
    // - if not all margin was removed from the max-height, add the remainder to the next item top position to move it down
    const bottomGap =
      cluster.expanded && cluster.items.length > 1
        ? Math.max(expandedClusterBottomMargin ?? 0, interClusterGap ?? 0, 0)
        : Math.max(interClusterGap ?? 0, 0)

    // Next item position without the bottom gap
    const nextItemTopNoGap = Math.max(nextItem.anchor.top, cluster.top + cluster.minContentHeight)
    // Cluster max-height without the bottom gap
    const clusterMaxHeightNoGap = nextItemTopNoGap - cluster.top

    // Cluster max-height, adjusted for the bottom gap, and also ensuring it is at least as large as the min-content-height
    cluster.maxContentHeight = Math.max(cluster.minContentHeight, clusterMaxHeightNoGap - bottomGap)

    // The remainder of the gap becomes a margin above the next item
    const appliedGap = clusterMaxHeightNoGap - cluster.maxContentHeight
    const bottomMargin = Math.max(0, bottomGap - appliedGap)

    // Shift the next item down by the remaining bottom margin
    const nextItemTop = nextItemTopNoGap + bottomMargin

    const lastItem = cluster.items.at(-1)
    if (lastItem) {
      lastItem.maxContentHeight = cluster.top + cluster.maxContentHeight - lastItem.top
    }

    return nextItemTop
  } else {
    const bottom = scrollHeight ?? Number.MIN_SAFE_INTEGER
    // Adjust max-height of last cluster and its last item
    cluster.maxContentHeight = Math.max(cluster.minContentHeight, bottom - cluster.top)
    const lastItem = cluster.items.at(-1)
    if (lastItem) {
      lastItem.maxContentHeight = Math.max(lastItem.minContentHeight, bottom - lastItem.top)
    }
  }
}
